import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import './Dashboard.css';

/* ── Micro charts con SVG (sin librería) ── */
const Sparkline = ({ data, color = '#82D8B3', height = 40 }) => {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const w = 100;
  const h = height;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((v - min) / range) * (h - 6) - 3;
    return `${x},${y}`;
  });
  const polyline = pts.join(' ');
  const area = `0,${h} ${polyline} ${w},${h}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="sparkline">
      <defs>
        <linearGradient id={`grad-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35"/>
          <stop offset="100%" stopColor={color} stopOpacity="0"/>
        </linearGradient>
      </defs>
      <polygon points={area} fill={`url(#grad-${color.replace('#','')})`}/>
      <polyline points={polyline} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
};

const DonutChart = ({ pct, color, size = 80 }) => {
  const r = 30; const cx = 40; const cy = 40;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <svg width={size} height={size} viewBox="0 0 80 80">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(130,216,179,.12)" strokeWidth="10"/>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth="10"
        strokeDasharray={`${dash} ${circ}`} strokeDashoffset={circ/4}
        strokeLinecap="round" style={{transition:'stroke-dasharray .8s ease'}}/>
      <text x={cx} y={cy+1} textAnchor="middle" dominantBaseline="middle"
        fill={color} fontSize="14" fontWeight="700" fontFamily="'DM Sans',sans-serif">
        {pct}%
      </text>
    </svg>
  );
};

/* ── Icons ── */
const IconTrendUp = () => (
  <svg viewBox="0 0 24 24" fill="none" width="14" height="14">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="17 6 23 6 23 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconTrendDown = () => (
  <svg viewBox="0 0 24 24" fill="none" width="14" height="14">
    <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="17 18 23 18 23 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconUsers = () => (
  <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
    <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
    <path d="M3 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M16 3.13a4 4 0 010 7.75M21 21v-2a4 4 0 00-3-3.87" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const IconVentas = () => (
  <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
    <line x1="12" y1="1" x2="12" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M17 5H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 110 7H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const IconFormaciones = () => (
  <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
    <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2zM22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconPuntos = () => (
  <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
  </svg>
);
const IconWhatsapp = () => (
  <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
    <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconWebsite = () => (
  <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
    <path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const IconCalendar = () => (
  <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
    <rect x="3" y="4" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="2"/>
    <path d="M3 9h18M8 2v4M16 2v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const IconBell = () => (
  <svg viewBox="0 0 24 24" fill="none" width="18" height="18">
    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconArrow = () => (
  <svg viewBox="0 0 20 20" fill="none" width="14" height="14">
    <path d="M4 10h12M11 6l5 4-5 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconSearch = () => (
  <svg viewBox="0 0 24 24" fill="none" width="17" height="17">
    <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
    <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const API_PAGA = 'https://centrocomunicar.com/api-comunicar/endpoints/recursos_paga.php';

const TIPO_COLORS = { recurso:'verde', kit:'dorado', juego:'azul', curso:'morado', otro:'gris' };

function fmt(n) {
  if (n >= 1000) return `S/. ${(n/1000).toFixed(1)}K`;
  return `S/. ${parseFloat(n).toFixed(2)}`;
}

function tiempoRelativo(fechaStr) {
  const diff = (new Date() - new Date(fechaStr)) / 1000;
  if (diff < 60)   return 'Hace un momento';
  if (diff < 3600) return `Hace ${Math.floor(diff/60)} min`;
  if (diff < 86400)return `Hace ${Math.floor(diff/3600)}h`;
  return `Hace ${Math.floor(diff/86400)}d`;
}

const IconSpinner = () => (
  <svg viewBox="0 0 24 24" fill="none" width="20" height="20" style={{animation:'spin .7s linear infinite'}}>
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeOpacity=".25"/>
    <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </svg>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [hora,  setHora]  = useState(new Date());
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setInterval(() => setHora(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    fetch(`${API_PAGA}?action=dashboard_stats`)
      .then(r => r.json())
      .then(j => { if (j.success) setStats(j.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const greeting = () => {
    const h = hora.getHours();
    if (h < 12) return 'Buenos días';
    if (h < 18) return 'Buenas tardes';
    return 'Buenas noches';
  };

  const fechaStr = hora.toLocaleDateString('es-PE', { weekday:'long', day:'numeric', month:'long' });
  const horaStr  = hora.toLocaleTimeString('es-PE', { hour:'2-digit', minute:'2-digit' });
  const mesActual = hora.toLocaleString('es-PE', { month:'long', year:'numeric' });

  // Sparkline de ingresos por mes
  const sparkMeses = stats?.ingresos_meses?.map(m => m.ingresos) || [0];
  const sparkSemana = stats?.spark_7dias?.map(d => parseFloat(d.ingresos)) || [0];

  const varIng = stats?.var_ingresos ?? 0;
  const varUsr = stats?.var_usuarios ?? 0;

  return (
    <div className="dash">

      {/* ── TOPBAR ── */}
      <div className="dash-topbar">
        <div className="dash-topbar__left">
          <div className="dash-topbar__search">
            <IconSearch />
            <input type="text" placeholder="Buscar usuarios, ventas, recursos…" />
          </div>
        </div>
        <div className="dash-topbar__right">
          <div className="dash-topbar__time">
            <IconCalendar />
            <span>{fechaStr} · <strong>{horaStr}</strong></span>
          </div>
          <button className="dash-topbar__bell" aria-label="Notificaciones">
            <IconBell />
            {stats?.pedidos_30dias?.por_validar > 0 && <span className="dash-topbar__bell-dot" />}
          </button>
          <a href="/" target="_blank" rel="noopener noreferrer" className="dash-topbar__site-btn">
            <IconWebsite /> Ver sitio
          </a>
        </div>
      </div>

      {/* ── HERO ── */}
      <div className="dash-hero">
        <div className="dash-hero__text">
          <p className="dash-hero__greeting">{greeting()}, <strong>{user?.name}</strong></p>
          <h1>Dashboard <em>Comunicar</em></h1>
          <p>
            {loading ? 'Cargando datos…' : (
              stats?.pedidos_30dias?.por_validar > 0
                ? <>Tienes <strong>{stats.pedidos_30dias.por_validar} pedido{stats.pedidos_30dias.por_validar > 1 ? 's' : ''} por validar</strong>. Revisa la sección de Pedidos.</>
                : <>Todo al día — sin pedidos pendientes. Mes: <strong>{mesActual}</strong>.</>
            )}
          </p>
        </div>
        <div className="dash-hero__quick">
          <a href="/admin/pedidos" className="dash-quick-btn dash-quick-btn--primary">
            Ver pedidos <IconArrow />
          </a>
          <a href="/admin/recursos" className="dash-quick-btn dash-quick-btn--ghost">
            + Recurso
          </a>
          <a href="/admin/formaciones" className="dash-quick-btn dash-quick-btn--ghost">
            + Formación
          </a>
        </div>
      </div>

      {loading ? (
        <div style={{display:'flex',alignItems:'center',justifyContent:'center',padding:'3rem',gap:'.8rem',color:'#5a6a63'}}>
          <IconSpinner/> Cargando estadísticas…
        </div>
      ) : (
        <>
          {/* ── KPI CARDS ── */}
          <div className="dash-kpis">

            <div className="kpi-card kpi-card--verde">
              <div className="kpi-card__top">
                <div className="kpi-card__icon kpi-icon--verde"><IconVentas /></div>
                <span className={`kpi-card__trend kpi-trend--${varIng >= 0 ? 'up' : 'down'}`}>
                  {varIng >= 0 ? <IconTrendUp/> : <IconTrendDown/>}
                  {varIng >= 0 ? '+' : ''}{varIng}%
                </span>
              </div>
              <p className="kpi-card__label">Ingresos del mes</p>
              <h2 className="kpi-card__value">S/. {parseFloat(stats?.ingresos_mes || 0).toFixed(2)}</h2>
              <div className="kpi-card__spark">
                <Sparkline data={sparkMeses.length > 1 ? sparkMeses : [0,1]} color="#82D8B3"/>
              </div>
              <p className="kpi-card__sub">vs S/. {parseFloat(stats?.ingresos_mes_anterior || 0).toFixed(2)} el mes pasado</p>
            </div>

            <div className="kpi-card">
              <div className="kpi-card__top">
                <div className="kpi-card__icon kpi-icon--azul"><IconUsers /></div>
                <span className={`kpi-card__trend kpi-trend--${varUsr >= 0 ? 'up' : 'down'}`}>
                  {varUsr >= 0 ? <IconTrendUp/> : <IconTrendDown/>}
                  {varUsr >= 0 ? '+' : ''}{varUsr}%
                </span>
              </div>
              <p className="kpi-card__label">Usuarios registrados</p>
              <h2 className="kpi-card__value">{parseInt(stats?.total_usuarios || 0).toLocaleString()}</h2>
              <div className="kpi-card__spark">
                <Sparkline data={[1,1,1,1,1,1,(stats?.usuarios_nuevos_semana||1)]} color="#60a5fa"/>
              </div>
              <p className="kpi-card__sub">+{stats?.usuarios_nuevos_semana || 0} nuevos esta semana</p>
            </div>

            <div className="kpi-card">
              <div className="kpi-card__top">
                <div className="kpi-card__icon kpi-icon--amarillo"><IconFormaciones /></div>
                <span className="kpi-card__trend kpi-trend--up">
                  <IconTrendUp/> {stats?.pedidos_30dias?.validados || 0} valid.
                </span>
              </div>
              <p className="kpi-card__label">Pedidos últimos 30 días</p>
              <h2 className="kpi-card__value">{parseInt(stats?.pedidos_30dias?.total || 0)}</h2>
              <div className="kpi-card__spark">
                <Sparkline data={sparkSemana.length > 1 ? sparkSemana : [0,1]} color="#fbbf24"/>
              </div>
              <p className="kpi-card__sub">
                {stats?.pedidos_30dias?.por_validar || 0} por validar · {stats?.pedidos_30dias?.rechazados || 0} rechazados
              </p>
            </div>

            <div className="kpi-card">
              <div className="kpi-card__top">
                <div className="kpi-card__icon kpi-icon--rosa"><IconPuntos /></div>
                <span className="kpi-card__trend kpi-trend--up">
                  <IconTrendUp/> {parseInt(stats?.puntos?.usuarios_con_puntos || 0)} usuarios
                </span>
              </div>
              <p className="kpi-card__label">Puntos en circulación</p>
              <h2 className="kpi-card__value">{parseInt(stats?.puntos?.total_disponibles || 0).toLocaleString()}</h2>
              <div className="kpi-card__spark">
                <Sparkline data={[1,1,1,1,1,1,(stats?.puntos?.total_disponibles||1)]} color="#f9a8d4"/>
              </div>
              <p className="kpi-card__sub">
                = S/. {((stats?.puntos?.total_disponibles || 0) / 100).toFixed(2)} en descuentos · {parseInt(stats?.puntos?.total_canjeados || 0).toLocaleString()} canjeados
              </p>
            </div>

          </div>

          {/* ── FILA MEDIA ── */}
          <div className="dash-mid">

            {/* Gráfica ingresos por mes */}
            <div className="dash-card dash-card--ventas">
              <div className="dash-card__head">
                <div>
                  <h3>Ingresos por mes</h3>
                  <p>Últimos 12 meses</p>
                </div>
                {varIng !== 0 && (
                  <span className={`dash-card__badge dash-card__badge--${varIng >= 0 ? 'up' : 'down'}`}>
                    {varIng >= 0 ? '+' : ''}{varIng}% vs mes anterior
                  </span>
                )}
              </div>
              <div className="bar-chart">
                {(stats?.ingresos_meses || []).length === 0 ? (
                  <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:'#5a6a63',fontSize:'.85rem'}}>
                    Sin datos de ingresos aún
                  </div>
                ) : stats.ingresos_meses.map((m, i) => {
                  const maxV = Math.max(...stats.ingresos_meses.map(x => x.ingresos), 1);
                  const pct  = (m.ingresos / maxV) * 100;
                  const isActive = i === stats.ingresos_meses.length - 1;
                  return (
                    <div key={m.mes} className={`bar-col ${isActive ? 'bar-col--active' : ''}`}>
                      <span className="bar-col__val">
                        {m.ingresos >= 1000 ? `S/${(m.ingresos/1000).toFixed(1)}k` : `S/${m.ingresos.toFixed(0)}`}
                      </span>
                      <div className="bar-col__wrap">
                        <div className="bar-col__fill" style={{height:`${Math.max(pct,2)}%`}} />
                      </div>
                      <span className="bar-col__mes">{m.mes_label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Pedidos por estado */}
            <div className="dash-card dash-card--canales">
              <div className="dash-card__head">
                <div>
                  <h3>Pedidos del mes</h3>
                  <p>Últimos 30 días</p>
                </div>
              </div>
              {stats?.pedidos_30dias && (() => {
                const total = parseInt(stats.pedidos_30dias.total) || 1;
                const val   = parseInt(stats.pedidos_30dias.validados) || 0;
                const pct   = Math.round((val / total) * 100);
                return (
                  <>
                    <div className="canales-wrap">
                      <div className="canal-donut">
                        <DonutChart pct={pct} color="#82D8B3" size={90}/>
                        <p className="canal-donut__label">Validados</p>
                      </div>
                      <div className="canal-donut">
                        <DonutChart pct={Math.round(((parseInt(stats.pedidos_30dias.por_validar)||0)/total)*100)} color="#fbbf24" size={90}/>
                        <p className="canal-donut__label">Por validar</p>
                      </div>
                    </div>
                    <div className="canales-detalle">
                      {[
                        { label:'Validados',       val: stats.pedidos_30dias.validados,       color:'verde'   },
                        { label:'Por validar',     val: stats.pedidos_30dias.por_validar,     color:'amarillo'},
                        { label:'Pendientes',      val: stats.pedidos_30dias.pendientes,      color:'gris'    },
                        { label:'Rechazados',      val: stats.pedidos_30dias.rechazados,      color:'rojo'    },
                      ].map(({label,val,color}) => (
                        <div className="canal-item" key={label}>
                          <span className="canal-dot" style={{background:
                            color==='verde'?'#82D8B3':color==='amarillo'?'#fbbf24':color==='rojo'?'#f87171':'#cbd5e1'
                          }}/>
                          <span>{label}</span>
                          <span className="canal-item__val">{val || 0}</span>
                          <span className="canal-item__pct">{Math.round(((parseInt(val)||0)/total)*100)}%</span>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
              <a href="/admin/pedidos" className="canales-whatsapp-cta" style={{marginTop:'1.2rem',display:'flex'}}>
                Ver todos los pedidos <IconArrow/>
              </a>
            </div>

          </div>

          {/* ── FILA BAJA ── */}
          <div className="dash-low">

            {/* Top recursos */}
            <div className="dash-card dash-card--recursos">
              <div className="dash-card__head">
                <div>
                  <h3>Top recursos vendidos</h3>
                  <p>Todos los tiempos</p>
                </div>
                <a href="/admin/recursos" className="dash-card__link">Ver todos <IconArrow /></a>
              </div>
              <div className="recursos-tabla">
                <div className="rt-head">
                  <span>Producto</span><span>Ventas</span><span>Ingresos</span>
                </div>
                {(stats?.top_recursos || []).length === 0 ? (
                  <div style={{padding:'1.5rem',textAlign:'center',color:'#5a6a63',fontSize:'.85rem'}}>
                    Sin ventas registradas aún
                  </div>
                ) : stats.top_recursos.map((r, i) => (
                  <div className="rt-row" key={r.id}>
                    <div className="rt-row__info">
                      <span className="rt-num">{i+1}</span>
                      <div>
                        <p>{r.titulo}</p>
                        <span className={`rt-tipo rt-tipo--${TIPO_COLORS[r.tipo] || 'verde'}`}>
                          {r.tipo}
                        </span>
                      </div>
                    </div>
                    <span className="rt-ventas">{r.ventas}</span>
                    <span className="rt-ing">S/. {parseFloat(r.ingresos_total).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Actividad reciente */}
            <div className="dash-card dash-card--actividad">
              <div className="dash-card__head">
                <div>
                  <h3>Actividad reciente</h3>
                  <p>Últimas 24 horas</p>
                </div>
              </div>
              <div className="actividad-feed">
                {(stats?.actividad_reciente || []).length === 0 ? (
                  <div style={{padding:'1rem',textAlign:'center',color:'#5a6a63',fontSize:'.85rem'}}>
                    Sin actividad en las últimas 24h
                  </div>
                ) : stats.actividad_reciente.map((a, i) => (
                  <div key={i} className={`act-item act-item--${a.tipo === 'usuario' ? 'usuario' : 'venta'}`}>
                    <div className={`act-dot act-dot--${a.tipo === 'usuario' ? 'usuario' : 'venta'}`} />
                    <div className="act-body">
                      <p>{a.desc_}</p>
                      <div className="act-meta">
                        <span className="act-hora">{tiempoRelativo(a.created_at)}</span>
                        {a.estado && a.estado !== 'nuevo' && (
                          <span className="act-canal act-canal--web">{a.estado}</span>
                        )}
                      </div>
                    </div>
                    {parseFloat(a.monto) > 0 && (
                      <span className="act-monto">S/. {parseFloat(a.monto).toFixed(2)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Resumen puntos */}
            <div className="dash-card dash-card--eventos">
              <div className="dash-card__head">
                <div>
                  <h3>Programa de puntos</h3>
                  <p>Estado actual</p>
                </div>
                <a href="/admin/puntos" className="dash-card__link">Gestionar <IconArrow /></a>
              </div>
              <div className="eventos-lista-dash">
                {[
                  { label:'Usuarios activos',    val: parseInt(stats?.puntos?.usuarios_con_puntos||0).toLocaleString(),  color:'#82D8B3' },
                  { label:'Pts disponibles',     val: parseInt(stats?.puntos?.total_disponibles||0).toLocaleString(),    color:'#4db88a' },
                  { label:'Pts acumulados total',val: parseInt(stats?.puntos?.total_acumulados||0).toLocaleString(),     color:'#60a5fa' },
                  { label:'Pts canjeados',       val: parseInt(stats?.puntos?.total_canjeados||0).toLocaleString(),      color:'#fbbf24' },
                ].map(({label, val, color}) => (
                  <div key={label} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'.6rem .8rem',background:'#f3f8f5',borderRadius:10}}>
                    <div style={{display:'flex',alignItems:'center',gap:'.6rem'}}>
                      <div style={{width:8,height:8,borderRadius:'50%',background:color,flexShrink:0}}/>
                      <span style={{fontSize:'.82rem',color:'#5a6a63'}}>{label}</span>
                    </div>
                    <strong style={{fontSize:'.9rem',color:'#111614'}}>{val}</strong>
                  </div>
                ))}
                <div style={{marginTop:'.5rem',padding:'.7rem .8rem',background:'#edfaf4',borderRadius:10,fontSize:'.8rem',color:'#4db88a',fontWeight:600}}>
                  💰 Equivale a S/. {((stats?.puntos?.total_disponibles||0)/100).toFixed(2)} en descuentos pendientes
                </div>
              </div>
            </div>

          </div>
        </>
      )}
    </div>
  );
}